import Chip from 'core-system/Chip'
import MonthToggle from 'core-system/DatePicker/MonthToggle'
import FlexContainer from 'core-system/FlexContainer'
import PlatformSvg from 'core-system/Icons/PlatformSvg'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import React, { useState } from 'react'
import { DailyTrip, MyCommuterDetails } from 'redux/employee/employeeTypes'
import FormattingUtils from 'shared/FormattingUtils'
import styled from 'styled-components'
import MyCommuterCalendar from './MyCommuterCalendar'
import MyCommuterTrip from './MyCommuterTrip'
import Box from 'core-system/Box/Box'

const MetricRow = styled(FlexContainer)`
  justify-content: space-between;
  margin-top: 0.75rem;
`

const Trips = styled.div``

const getStartDate = (currentDate: string) => {
  const thisMonth = moment().format('YYYY-MM')
  if (thisMonth === currentDate) {
    return moment().date()
  } else {
    return moment(currentDate).endOf('month').date()
  }
}

const renderTrips = (
  tripsByDay: Dictionary<DailyTrip[]>,
  daySelected: number
) => {
  const hasNoTripReason =
    tripsByDay[daySelected] &&
    tripsByDay[daySelected].find((trip) => trip.noTripReason)
  if (!tripsByDay[daySelected] || hasNoTripReason) {
    return (
      <FlexContainer width='100%' height='100%' flexDirection='column' center>
        <PlatformSvg folder='segments' variant='noTrip' />
        <Text
          variant='action1'
          textColor={palette.text.secondary}
          marginTop='2rem'
        >
          No Trips Logged
        </Text>
        {hasNoTripReason && (
          <Chip
            variant='filter'
            selected={true}
            marginTop='1rem'
            isInteractive={false}
          >
            {hasNoTripReason.noTripReason}
          </Chip>
        )}
      </FlexContainer>
    )
  } else if (tripsByDay[daySelected] && tripsByDay[daySelected].length > 0) {
    return tripsByDay[daySelected].map((trip, idx) => (
      <MyCommuterTrip key={idx} trip={trip} marginTop='0.5rem' />
    ))
  } else {
    return null
  }
}

interface MyCommuterDetailsTripsProps {
  currentDate: string
  onIncreaseClick: () => void
  onDecreaseClick: () => void
  commuterDetails: MyCommuterDetails
}

const MyCommuterDetailsTrips = React.memo(
  (props: MyCommuterDetailsTripsProps) => {
    const { currentDate, commuterDetails, onIncreaseClick, onDecreaseClick } =
      props

    const [daySelected, setDaySelected] = useState(getStartDate(currentDate))

    const tripsByDay = Object.keys(commuterDetails.tripsByDay).reduce(
      (agg, key) => {
        agg[key] = commuterDetails.tripsByDay[key].length
        return agg
      },
      {}
    )

    const budgetSpend = commuterDetails.programData.reduce(
      (agg, program) => (agg += program.totalSpend ? program.totalSpend : 0),
      0
    )

    const handleSetActiveTripStats = (dayNumber: number) => {
      if (dayNumber !== daySelected) {
        setDaySelected(dayNumber)
      }
    }

    return (
      <FlexContainer>
        <FlexContainer width='50%' flexDirection='column' marginRight='1rem'>
          <Box style={{ padding: '1rem', marginBottom: '1rem' }}>
            <MonthToggle
              currentDate={currentDate}
              onIncreaseClick={onIncreaseClick}
              onDecreaseClick={onDecreaseClick}
            />
            <MyCommuterCalendar
              marginTop='1rem'
              setActiveTripStats={handleSetActiveTripStats}
              daySelected={daySelected}
              tripsByDay={tripsByDay}
              firstDayOfMonth={moment(currentDate).startOf('month')}
              currentMonth={moment(currentDate).format('YYYY-MM')}
            />
          </Box>
          <Box style={{ padding: '1rem' }}>
            <MetricRow>
              <Text variant='action4'>Trips Logged</Text>
              <Text variant='action4'>
                {FormattingUtils.formatNumber(commuterDetails.tripsThisMonth)}
              </Text>
            </MetricRow>
            <MetricRow>
              <Text variant='action4'>Budget Spend</Text>
              <Text variant='action4'>
                {FormattingUtils.formatDollar(budgetSpend || 0)}
              </Text>
            </MetricRow>
            <MetricRow>
              <Text variant='action4'>Rewards Spent</Text>
              <Text variant='action4'>
                {FormattingUtils.formatDollar(
                  commuterDetails.rewardsUtilization || 0
                )}
              </Text>
            </MetricRow>
          </Box>
        </FlexContainer>
        <FlexContainer width='50%' flexDirection='column'>
          <Box style={{ padding: '1rem' }}>
            <Text variant='action2' margin='0.3125rem 0 0.5rem'>
              {moment(`${currentDate}-${daySelected}`).format('MMMM Do, YYYY')}
            </Text>
            <Trips>
              {renderTrips(commuterDetails.tripsByDay, daySelected)}
            </Trips>
          </Box>
        </FlexContainer>
      </FlexContainer>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommuterDetailsTrips.displayName = 'MyCommuterDetailsTrips'
}

export default MyCommuterDetailsTrips
